import type { ComponentType } from "react"
import { z } from "zod"

export type Point = {
  latitude: number;
  longitude: number;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WidgetActionHandler = (action: string, data?: any) => void;

// Base widget props that all widgets receive
export interface BaseWidgetProps {
  onAction?: WidgetActionHandler;
}

// Widget component type with base props
export type WidgetComponent<T = Record<string, unknown>> = ComponentType<
  T & BaseWidgetProps
>;

// Widget registration entry
export interface WidgetRegistration<T = Record<string, unknown>> {
  component: WidgetComponent<T>;
  schema: z.ZodType<T>;
}

// Doctor Card widget data
export const DoctorCardDataSchema = z.object({
  id: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  photoUrl: z.string().nullish().optional(),
  city: z.string().optional(),
  stateAbbr: z.string().optional(),
  distanceMiles: z.number().optional(),
})

export type DoctorCardData = z.infer<typeof DoctorCardDataSchema>;

export interface DoctorCardProps extends BaseWidgetProps {
  data: DoctorCardData;
}

export const CostCompareDataSchema = z.object({
  procedureName: z.string(),
  averageLanternCost: z.number(),
  healthInsuranceCost: z.number(),
})

export type CostCompareData = z.infer<typeof CostCompareDataSchema>;

export interface CostCompareProps extends BaseWidgetProps {
  data: CostCompareData;
}

// Appointment Card widget data, not used currently
export const AppointmentCardDataSchema = z.object({
  id: z.string(),
  doctorName: z.string(),
  specialty: z.string(),
  date: z.string(),
  time: z.string(),
  location: z.string(),
  status: z.enum(["scheduled", "confirmed", "cancelled", "completed"]),
})

export type AppointmentCardData = z.infer<typeof AppointmentCardDataSchema>;

export interface AppointmentCardProps extends BaseWidgetProps {
  data: AppointmentCardData;
}

export const ProviderListDataSchema = z.array(DoctorCardDataSchema)
export type ProviderListData = z.infer<typeof ProviderListDataSchema>;

export interface ProviderListProps extends BaseWidgetProps {
  data: ProviderListData;
}

// Covered Procedures widget data (category -> procedures)
export const CoveredProceduresWidgetDataSchema = z.object({
  categories: z.array(
    z.object({
      name: z.string(),
      procedures: z.array(z.string()),
    }),
  ),
})

export type CoveredProceduresWidgetData = z.infer<
  typeof CoveredProceduresWidgetDataSchema
>;

export interface CoveredProceduresWidgetProps extends BaseWidgetProps {
  data: CoveredProceduresWidgetData;
}

// Care Advocate callback slot picker widget data. Labels arrive preformatted
// from the API (Central Time); slotStartUtc is the opaque booking key echoed
// back on selection — the client does no timezone math.
export const CareAdvocateCallbackSlotSchema = z.object({
  localLabel: z.string(),
  slotStartUtc: z.string(),
})

export type CareAdvocateCallbackSlot = z.infer<typeof CareAdvocateCallbackSlotSchema>;

export const CareAdvocateCallbackSlotPickerDataSchema = z.object({
  // Defaulted rather than required so a payload written before rescheduling
  // existed still parses instead of falling back to the generic widget.
  mode: z.enum(["book", "reschedule"]).default("book"),
  currentBookingLabel: z.string().nullish(),
  timezoneNote: z.string().default(""),
  slotDurationMinutes: z.number().nullish(),
  days: z.array(
    z.object({
      dayLabel: z.string(),
      slots: z.array(CareAdvocateCallbackSlotSchema),
    }),
  ),
})

export type CareAdvocateCallbackSlotPickerData = z.infer<
  typeof CareAdvocateCallbackSlotPickerDataSchema
>;

export interface CareAdvocateCallbackSlotPickerProps extends BaseWidgetProps {
  data: CareAdvocateCallbackSlotPickerData;
}

// Widget action emitted when a member taps a callback slot; the chat layer
// turns it into a booking message.
export const BOOK_CAREADVOCATE_CALLBACK_SLOT_ACTION = "book_careadvocate_callback_slot"

// Moving a call is a distinct action rather than a flag on the booking one, so the
// chat layer turns each tap into the message that says what the member meant.
export const RESCHEDULE_CAREADVOCATE_CALLBACK_SLOT_ACTION =
  "reschedule_careadvocate_callback"

// The member's upcoming Care Advocate calls, shown for display ("view") or for
// tapping the one to move ("reschedule") or cancel ("cancel"). Labels arrive
// formatted from the API; callbackId is echoed back on selection.
export const CareAdvocateCallbackSchema = z.object({
  callbackId: z.number(),
  label: z.string(),
})

export type CareAdvocateCallback = z.infer<typeof CareAdvocateCallbackSchema>;

export const CareAdvocateCallbackPickerDataSchema = z.object({
  mode: z.enum(["view", "reschedule", "cancel"]).default("view"),
  callbacks: z.array(CareAdvocateCallbackSchema),
})

export type CareAdvocateCallbackPickerData = z.infer<
  typeof CareAdvocateCallbackPickerDataSchema
>;

export interface CareAdvocateCallbackPickerProps extends BaseWidgetProps {
  data: CareAdvocateCallbackPickerData;
}

// Widget actions emitted when a member taps one of their calls; the chat layer
// turns each into the message naming the call to move or cancel.
export const CHOOSE_CAREADVOCATE_CALLBACK_TO_RESCHEDULE_ACTION =
  "choose_careadvocate_callback_to_reschedule"
export const CHOOSE_CAREADVOCATE_CALLBACK_TO_CANCEL_ACTION =
  "choose_careadvocate_callback_to_cancel"

// Generic widget renderer props
export interface WidgetRendererProps {
  type: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  onAction?: WidgetActionHandler;
}

// Fallback widget props
export interface FallbackWidgetProps extends BaseWidgetProps {
  type: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}
